<template>
  <div class="create-listing-form">

    <div class="grey-box-container clearfix" v-if="editMode">
      <h2>Share your auction</h2>
      <div>
        <a style="float:right;" @click.prevent="copyToClipboard">{{ copyLinkText }}</a>
        Use this URL to share your auction:
        <a :href="absoluteAuctionUrl(auction)" target="_blank" style="text-decoration: none; color: inherit;">
          <span style="font-weight: 600;">{{ absoluteAuctionUrl(auction) }}</span>
        </a>
      </div>
    </div>

    <div class="grey-box-container" style="background-color: #E8F1F4">
      <h2>Preview your auction</h2>
      <div>This is what your audience will see. Data is not yet saved.</div>
    </div>

    <div>
      <AuctionPage :preview-auction="auction" :disable-links="true" />
    </div>

    <hr/><br/>

  </div>
</template>

<script>
  import AuctionPage from './AuctionPage.vue'
  import { absoluteAuctionUrl, auctionInvitationRoles } from '../utils/PfxUtils.js'
  import { mapGetters } from 'vuex'

  export default {
    name: 'CreateListingPreview',
    components: {
      AuctionPage
    },
    data() {
      return {
        copyLinkText: 'Copy to clipboard',
        auction: {},
        files: [],
        buyerRequirements: [],
        invitedSellerEmail: ''
      }
    },
    computed: {
      ...mapGetters([
        'getFilesForNewAuction',
        'subdomain',
      ]),
      editMode() {
        return this.$store.state.newAuctionStorage.edit === true
      }
    },
    created() {
      this.auction = this.$store.state.newAuctionStorage.auction
      this.auction.metadata = this.$store.state.newAuctionStorage.auctionMetadata
      this.auction.tenant = this.$store.state.tenant.data

      // get invited but pending seller (if any)
      if ( this.auction.id !== 0) {
        this.$PfxBackendApi.getAuctionInvitations(this.subdomain, this.auction.id)
          .then(res => {
            console.debug('getAuctionInvitations res', res)
            const invitations = res.data || []
            const sellerRoles = invitations.filter(i => i.auctionInvitationRole === 'SELLER')
            if (sellerRoles.length > 0) {
              this.invitedSellerEmail = sellerRoles[0].email
              console.log('invitedSellerEmail [%s]', this.invitedSellerEmail)
            }
          })
          .catch(err => console.error('getAuctionInvitations err', err))
      }
      // convert [ "foo", "bar" ] into [ { name: "foo" }, { name: "bar" } ]
      const buyerRequirements = []
      this.$store.state.newAuctionStorage.buyerRequirements.forEach(req => {
        buyerRequirements.push({ docType: req })
      })
      this.buyerRequirements = buyerRequirements
    },
    methods: {
      absoluteAuctionUrl,

      copyToClipboard() {
        const el = document.createElement('textarea')
        el.value = absoluteAuctionUrl(this.auction)
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        console.log('copyToClipboard done [%s]', el.value)

        this.copyLinkText = ''
        setTimeout(() => this.copyLinkText = 'Copy to clipboard', 1000)
      },

      uploadFiles(auctionId, files) {
        const that = this
        return new Promise((resolve, reject) => {
          let file = files.pop()
          
          if (file !== undefined) {
            file.docType = 99
            console.log('fileUpload request [%s]', file.name)
            this.$PfxBackendApi.fileUpload(auctionId, file).then(res => {
              console.log('fileUpload res', res)
            }).catch(err => {
              console.error('fileUpload err for [%s]', file.name, JSON.stringify(err))
            }).then(() => {
              that.uploadFiles(auctionId, files) // recurse to handle next file
                .then(() => resolve()).catch(() => reject())
            })
          } else {
            console.log('uploadFiles: all files uploaded')
            resolve()
          }
        })
      },

      setBuyerRequirements(auctionId, buyerRequirements) {
        return new Promise((resolve, reject) => {
          const data = { buyerRequirements }
          this.$PfxBackendApi.putAuctionBuyerRequirements(auctionId, data).then(res => {
            console.log('putAuctionBuyerRequirements res', res)
            resolve()
          }).catch(err => {
            console.error('putAuctionBuyerRequirements err', err)
            this.$notify({ type: 'danger', message: 'Unexpected error, cannot update buyer required uploads' })
            reject()
          })
        })
      },

      saveNewPhotos(auctionId, photosAdded) {
        return new Promise((resolve, reject) => {
          photosAdded = photosAdded.filter(p => !p.removed)
          photosAdded.forEach(p => {
            delete p.removed
          })

          if (photosAdded.length > 0) {
            const photoAdded = photosAdded[0]
            console.log('postAuctionPhotos request [%s][%s]', photoAdded.name, photoAdded.size)
            this.$PfxBackendApi.postAuctionPhotos(auctionId, photoAdded).then(res => {
              console.log('postAuctionPhotos res', res)
            }).catch(err => {
              console.error('postAuctionPhotos err', err)
              this.$notify({ type: 'danger', message: 'Unexpected error, cannot add new property photo: ' + photosAdded.name })
              reject()
            }).then(() => {
              photosAdded.splice(0, 1)
              this.saveNewPhotos(auctionId, photosAdded) // recurse (why? add each photo separately, since the 10MB hard limit of API Gateway payload)
                .then(() => resolve())
                .catch(() => reject())
            })
          } else {
            resolve()
          }
        })
      },

      removePhotos(photoUrls) {
        return new Promise((resolve, reject) => {
          if (photoUrls && Array.isArray(photoUrls) && photoUrls.length > 0) {
            this.$PfxBackendApi.deleteAuctionPhotos(photoUrls).then(res => {
              console.log('deleteAuctionPhotos res', res)
              resolve()
            }).catch(err => {
              console.error('deleteAuctionPhotos err', err)
              this.$notify({ type: 'danger', message: 'Unexpected error, cannot delete property photos' })
              reject()
            })
          } else {
            resolve()
          }
        })
      },

      sendSellerInvitation(auctionId, agentAuctionRoleId, sellerEmail) {
        return new Promise((resolve, reject) => {
          if (sellerEmail && sellerEmail !== this.invitedSellerEmail) {
            this.$PfxBackendApi.createAuctionInvitation(this.subdomain, auctionId, agentAuctionRoleId, auctionInvitationRoles.SELLER.value, sellerEmail)
              .then(res => {
                console.log('createAuctionInvitation res', res)
                resolve()
              })
              .catch(err => {
                console.error('createAuctionInvitation err', err)
                this.$notify({ type: 'danger', message: 'Unexpected error, cannot create seller invitation' })
                reject()
              })
          } else {
            console.log('skipped sendSellerInvitation since sellerEmail [%s] invitedSellerEmail [%s]', sellerEmail, this.invitedSellerEmail)
            resolve()
          }
        })
      },

      nextStep() {
        const that = this
        that.$store.commit('setLoadingSpinner', true)

        // remove existing photo this is marked as removed from the photos array
        this.auction.metadata.photosRemoved.forEach(photoRemoved => {
          const i = this.auction.metadata.photos.indexOf(photoRemoved)
          if (i > -1) this.auction.metadata.photos.splice(i, 1)
        })
        const removePhotos = this.auction.metadata.photosRemoved
        delete this.auction.metadata.photosRemoved

        const newPhotos = this.auction.metadata.photosAdded
        delete this.auction.metadata.photosAdded

        // pick invite seller email (if any)
        const sellerEmail = this.auction.sellerEmail
        delete this.auction.sellerEmail

        if (this.editMode) {
          console.log('putAuction data (auction)', this.auction)
          delete this.auction.tenant

          this.$PfxBackendApi.putAuction(this.auction.id, this.auction).then(res => {
            console.log('putAuction res', res)
            Promise.all([
              that.setBuyerRequirements(this.auction.id, that.buyerRequirements), // PUT the buyer requirements
              that.saveNewPhotos(this.auction.id, newPhotos),
              that.removePhotos(removePhotos),
              that.sendSellerInvitation(this.auction.id, res.data.role.id, sellerEmail)
            ]).then(() => {
              that.$store.commit('clearNewAuctionStorage')
              that.$store.commit('setLoadingSpinner', false)
              that.$router.push('/dashboard')
            }).catch(() => {
              that.$store.commit('setLoadingSpinner', false)
            })
          }).catch(err => {
            that.$store.commit('setLoadingSpinner', false)
            this.goToPropertyTabAndRefresh()
          })

        } else {
          this.$PfxBackendApi.postAuction(this.auction).then(res => {
            console.log('postAuction res', res)
            // Auction is now saved -> ID is known and corresponding role is created
            const newAuctionId = res.data.id
            Promise.all([
              that.uploadFiles(newAuctionId, that.getFilesForNewAuction), // upload files held in store
              that.setBuyerRequirements(newAuctionId, that.buyerRequirements), // PUT the buyer requirements
              that.saveNewPhotos(newAuctionId, newPhotos),
              that.removePhotos(removePhotos),
              that.sendSellerInvitation(newAuctionId, res.data.role.id, sellerEmail)
            ]).then(() => {
              that.$store.commit('clearNewAuctionStorage')
              that.$store.commit('setLoadingSpinner', false)
              that.$router.push('/dashboard')
            })
          }).catch(err => {
            console.error('postAuction err', err)
            that.$store.commit('setLoadingSpinner', false)
            this.$notify({type: 'danger', message: 'Unexpected error, cannot insert auction information'})
          })
        }
      },

      goToPropertyTabAndRefresh(){
        this.$store.commit('setCreateListingProgressCurrentStep', 0)
        window.scrollTo(0,0)
        this.$parent.getData()
        this.$notify({type: 'danger', message: 'This auction is currently out of date. Please check the new details and try to update again'})
      }
    }
  }
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/CreateListingPreview.vue